import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PortableText } from '@portabletext/react';
import { getPortfolioItemBySlug, getOptimizedImageUrl } from '../sanity/client';
import SocialShare from '../components/SocialShare';
import './PortfolioItemPage.css';

const portableTextComponents = {
  block: {
    h2: ({ children }) => <h2 className="portfolio-h2">{children}</h2>,
    h3: ({ children }) => <h3 className="portfolio-h3">{children}</h3>,
    blockquote: ({ children }) => <blockquote className="portfolio-quote">{children}</blockquote>,
    normal: ({ children }) => <p className="portfolio-paragraph">{children}</p>,
  },
  marks: {
    link: ({ children, value }) => (
      <a href={value?.href} target="_blank" rel="noopener noreferrer" className="portfolio-link">
        {children}
      </a>
    ),
  },
};

const PortfolioItemPage = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  // Get localized title and content based on current language
  const getLocalizedField = (fieldName) => {
    if (!item) return '';
    const lang = i18n.language === 'it' ? 'It' : 'En';
    return item[`${fieldName}${lang}`] || item[`${fieldName}En`];
  };

  const title = getLocalizedField('title');
  const content = getLocalizedField('content');

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (item) {
      document.title = `${getLocalizedField('title')} - Magicomms`;
    }
  }, [item, i18n.language]);

  useEffect(() => {
    // Scroll to top when component mounts or slug changes
    window.scrollTo(0, 0);
    
    const fetchItem = async () => {
      try {
        const data = await getPortfolioItemBySlug(slug);
        if (!data) {
          setError('Portfolio item not found');
        } else {
          setItem(data);
        }
      } catch (err) {
        console.error('Error fetching portfolio item:', err);
        setError('Failed to load portfolio item');
      } finally {
        setLoading(false);
      }
    };

    fetchItem();
  }, [slug]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    });
  };

  const getCategoryLabel = (category) => {
    const labels = {
      branding: 'Branding',
      digital: 'Digital',
      events: 'Events',
      pr: 'PR',
      social: 'Social Media',
      other: 'Other'
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <div className="portfolio-item-page">
        <div className="portfolio-item-container">
          <div className="portfolio-item-loading">
            <div className="skeleton-back"></div>
            <div className="skeleton-meta"></div>
            <div className="skeleton-title-large"></div>
            <div className="skeleton-image-large"></div>
            <div className="skeleton-content"></div>
            <div className="skeleton-content"></div>
            <div className="skeleton-content short"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="portfolio-item-page">
        <div className="portfolio-item-container">
          <button onClick={handleBack} className="portfolio-item-back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            {t('common.back')}
          </button>
          <p className="portfolio-item-error">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="portfolio-item-page">
      <div className="portfolio-item-container">
        <article className="portfolio-item-content">
          <header className="portfolio-item-header">
            <div className="portfolio-item-header-top">
              <button onClick={handleBack} className="portfolio-item-back">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                {t('common.back')}
              </button>
              {item.publishedDate && (
                <time className="portfolio-item-date">{formatDate(item.publishedDate)}</time>
              )}
            </div>
            <h1 className="portfolio-item-title">{title}</h1>
          </header>

          {/* Main images section - show mainImage and secondImage */}
          {(item.mainImage?.asset?.url || item.secondImage?.asset?.url) && (
            <div className="portfolio-item-main-images">
              {item.mainImage?.asset?.url && (
                <div className="portfolio-item-main-image">
                  <img 
                    src={getOptimizedImageUrl(item.mainImage, { width: 1200, quality: 90 })} 
                    alt={title}
                    onClick={() => setLightboxImage(getOptimizedImageUrl(item.mainImage, { width: 1920, quality: 95 }))}
                  />
                </div>
              )}
              {item.secondImage?.asset?.url && (
                <div className="portfolio-item-main-image">
                  <img 
                    src={getOptimizedImageUrl(item.secondImage, { width: 1200, quality: 90 })} 
                    alt={title}
                    onClick={() => setLightboxImage(getOptimizedImageUrl(item.secondImage, { width: 1920, quality: 95 }))}
                  />
                </div>
              )}
            </div>
          )}

          {content && (
            <div className="portfolio-item-body">
              <PortableText value={content} components={portableTextComponents} />
            </div>
          )}

          {item.additionalImages && item.additionalImages.length > 0 && (
            <div className="portfolio-item-gallery">
              <h3 className="gallery-title">{t('portfolio.projectGallery')}</h3>
              <div className="gallery-grid">
                {item.additionalImages.map((img, index) => (
                  <div 
                    key={index} 
                    className="gallery-item"
                    onClick={() => setLightboxImage(getOptimizedImageUrl(img, { width: 1920, quality: 95 }))}
                  >
                    <img src={getOptimizedImageUrl(img, { width: 800, quality: 85 })} alt={`${title} - Image ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <SocialShare 
            url={window.location.href}
            title={title}
            description={title}
          />
        </article>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div className="lightbox" onClick={() => setLightboxImage(null)}>
          <button className="lightbox-close" onClick={() => setLightboxImage(null)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
          <img src={lightboxImage} alt="Full size" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default PortfolioItemPage;
